import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Drawer, DatePicker } from "antd";
import Select from "react-select";
import "./Apply-Leave-drawer.scss";
import moment from "moment";
import SelectInput from "../../atoms/SelectInput/SelectInput";
import Input from "../../atoms/Input/Input";
import AddButton from "../../atoms/AddButton/AddButton";
import Loader from "../../atoms/Loader/Loader";
import ErrorText from "../../atoms/Error/errorText";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import {
  createLeaveRequestAPI,
  getLeaveRequestPreviewAPI,
  getMyLeaveBalanceAPI,
  updateOwnLeaveRequestAPI,
} from "../../../Redux/Apis/leaveApi";
import { useDispatch } from "react-redux";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { checkAnyPermission, permissionType } from "../../../app/utils/common/Functions";
import { isLeaveStartAfterAccrualStop, resolveAvailablePaidLeavesForRequest } from "../../../app/utils/common/paidLeaveAccrual";
import {
  LEAVE_CATEGORY_OTHER,
  PRESET_LEAVE_CATEGORIES,
} from "../../../app/utils/constants/option";

const toHm = (t) => {
  if (t == null || t === "") return "";
  const s = String(t).trim();
  return s.length >= 5 ? s.slice(0, 5) : s;
};
const isEarlyLeave = (leaveType) => String(leaveType || "").trim().toLowerCase() === "early leave";

/** For WFH requests, backend fields leaveType and leaveCategory should both be WFH. */
const WFH_REQUEST_TYPE = "WFH";
const MAX_CONTINUOUS_PAID_LEAVES = 6;
const MAX_CUSTOM_LEAVE_CATEGORY_LENGTH = 100;
const LEAVE_PREVIEW_DEBOUNCE_MS = 250;

const formatPreviewDate = (dateStr) => moment(dateStr, "YYYY-MM-DD").format("ddd, DD MMM");
const formatPreviewMonth = (yearMonth) => moment(yearMonth, "YYYY-MM").format("MMM YYYY");
const formatDayCount = (value) => {
  const n = Number(value || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};
const isPreviewAllNonWorking = (preview) =>
  Boolean(preview) &&
  preview.calendarDays > 0 &&
  (preview.weekendDates?.length || 0) + (preview.holidays?.length || 0) >= preview.calendarDays;

const resolveLeaveCategorySelection = (category) => {
  const value = String(category || "").trim();
  if (!value) return { leaveCategory: null, customLeaveCategory: "" };
  if (PRESET_LEAVE_CATEGORIES.has(value)) {
    return { leaveCategory: value, customLeaveCategory: "" };
  }
  if (value === LEAVE_CATEGORY_OTHER) {
    return { leaveCategory: LEAVE_CATEGORY_OTHER, customLeaveCategory: "" };
  }
  return { leaveCategory: LEAVE_CATEGORY_OTHER, customLeaveCategory: value };
};

const LeaveDayCountNotice = ({ preview, loading, showPaidSplit }) => {
  if (loading || !preview) {
    return <p className="m-0 text-xs text-gray-500">Calculating leave days...</p>;
  }

  const weekendDates = preview.weekendDates || [];
  const holidays = preview.holidays || [];
  const excludedCount = weekendDates.length + holidays.length;
  const months = preview.months || [];
  const currentYearMonth = moment().format("YYYY-MM");
  const showMonthBreakdown =
    showPaidSplit && (months.length > 1 || months.some((m) => m.yearMonth > currentYearMonth));

  if (isPreviewAllNonWorking(preview)) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
        All selected date(s) are weekends or public holidays, so no leave is required.
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border p-3 text-xs flex flex-col gap-1 ${excludedCount > 0
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-indigo-100 bg-indigo-50 text-indigo-900"
        }`}
    >
      {excludedCount > 0 ? (
        <>
          <p className="m-0">
            You selected {preview.calendarDays} day(s).{" "}
            {[
              weekendDates.length > 0 ? `${weekendDates.length} weekend day(s)` : null,
              holidays.length > 0 ? `${holidays.length} public holiday(s)` : null,
            ]
              .filter(Boolean)
              .join(" and ")}{" "}
            in this range {excludedCount === 1 ? "is" : "are"} not counted as leave.
          </p>
          {weekendDates.length > 0 && (
            <p className="m-0">Weekend: {weekendDates.map(formatPreviewDate).join(", ")}</p>
          )}
          {holidays.length > 0 && (
            <p className="m-0">
              Public holiday:{" "}
              {holidays.map((h) => `${h.name} (${formatPreviewDate(h.date)})`).join(", ")}
            </p>
          )}
        </>
      ) : null}
      <p className="m-0 font-semibold">Leave days counted: {formatDayCount(preview.leaveDays)}</p>
      {showPaidSplit && Number(preview.leaveDays) > 0 && (
        <p className="m-0">
          Paid: {formatDayCount(preview.paidDays)} day(s)
          {Number(preview.unpaidDays) > 0 ? ` | Unpaid: ${formatDayCount(preview.unpaidDays)} day(s)` : ""}
        </p>
      )}
      {showMonthBreakdown && (
        <>
          {months.map((m) => (
            <p key={m.yearMonth} className="m-0">
              {formatPreviewMonth(m.yearMonth)}: {formatDayCount(m.leaveDays)} day(s) —{" "}
              {formatDayCount(m.paidDays)} paid
              {Number(m.unpaidDays) > 0 ? `, ${formatDayCount(m.unpaidDays)} unpaid` : ""}
            </p>
          ))}
          {/* <p className="m-0 opacity-80">
            Each month adds 1 paid leave, so leave in a future month can use that month&apos;s paid leave.
          </p> */}
        </>
      )}
    </div>
  );
};

const ApplyLeavedrawer = ({ toggleLeaveDrawer, isOpen, editingLeave, initialValues }) => {
  // Updated state variables based on payload
  const [leaveType, setLeaveType] = useState(null);
  const [leaveCategory, setLeaveCategory] = useState(null);
  const [customLeaveCategory, setCustomLeaveCategory] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState("");
  const [startDaySection, setStartDaySection] = useState(leaveType === "Half Leave" ? { value: "First Half", label: "First Half" } : { value: "Full Day", label: "Full Day" });
  const [endDaySection, setEndDaySection] = useState(leaveType === "Half Leave" ? { value: "Second Half", label: "Second Half" } : { value: "Full Day", label: "Full Day" });
  const [reason, setReason] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [wfhDayKind, setWfhDayKind] = useState("full");
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(false);
  const [showEarlyLeaveOverrideModal, setShowEarlyLeaveOverrideModal] = useState(false);
  const [showContinuousPaidLeaveModal, setShowContinuousPaidLeaveModal] = useState(false);
  const [showUnpaidLeaveModal, setShowUnpaidLeaveModal] = useState(false);
  const [continuousLeaveSplitInfo, setContinuousLeaveSplitInfo] = useState(null);
  const [unpaidLeaveInfo, setUnpaidLeaveInfo] = useState(null);
  const [leavePreview, setLeavePreview] = useState(null);
  const [leavePreviewLoading, setLeavePreviewLoading] = useState(false);
  const dispatch = useDispatch();
  const isEmployeeUser = !checkAnyPermission(permissionType.LEAVE_APPROVE, [
    "read",
    "create",
    "update",
    "delete",
    "read users",
  ]);

  const isEditMode = Boolean(editingLeave?.leaveRequestId);
  const isWfhSimplified = useMemo(() => {
    const draftWfh = String(initialValues?.workLocation || "").toLowerCase() === "wfh";
    const editingWfh = String(editingLeave?.workLocation || "").toLowerCase() === "wfh";
    if (isEditMode) return editingWfh;
    return draftWfh;
  }, [isEditMode, initialValues, editingLeave]);
  const today = useMemo(() => moment().startOf("day"), []);
  const dateFloor = useMemo(() => {
    if (!editingLeave?.startDate) return today;
    const orig = moment(editingLeave.startDate).startOf("day");
    return orig.isBefore(today) ? orig : today;
  }, [editingLeave, today]);

  // Show/hide fields based on leave type
  const showStartTime = leaveType === "Early Leave";
  const showEndDate = leaveType === "Multiple Days Leave";
  const showEndTime = leaveType === "Early Leave";
  const showDaySections = leaveType === "Multiple Days Leave" || leaveType === "Half Leave";
  const showHalfDayOptions = leaveType === "Half Leave";
  const earlyLeaveEligibleThisMonth = Boolean(leaveBalance?.earlyLeaveEligibleThisMonth);
  const monthlyEarlyLeaveUsedCount = Number(leaveBalance?.monthlyEarlyLeaveUsedCount ?? 0);
  const earlyLeaveUsageKnown =
    leaveBalance != null &&
    (leaveBalance.earlyLeaveEligibleThisMonth !== undefined ||
      leaveBalance.monthlyEarlyLeaveUsedCount !== undefined);
  const earlyLeaveAlreadyUsedForNewApply =
    !isWfhSimplified &&
    !isEditMode &&
    earlyLeaveUsageKnown &&
    (monthlyEarlyLeaveUsedCount >= 1 || !earlyLeaveEligibleThisMonth);
  const showEarlyLeaveNotice = !isWfhSimplified && isEarlyLeave(leaveType);

  const applyLeaveTypeDayDefaults = useCallback((lt) => {
    if (lt === "Half Leave") {
      setStartDaySection({ value: "First Half", label: "First Half (9:00 AM - 1:00 PM)" });
      setEndDaySection({ value: "Second Half", label: "Second Half (2:00 PM - 6:00 PM)" });
    } else {
      setStartDaySection({ value: "Full Day", label: "Full Day" });
      setEndDaySection({ value: "Full Day", label: "Full Day" });
    }
  }, []);

  // Reset form fields when drawer closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchLeaveBalance = async () => {
      if (!isOpen) return;
      try {
        setLeaveBalanceLoading(true);
        const response = await getMyLeaveBalanceAPI();
        setLeaveBalance(response?.data?.data ?? response?.data ?? null);
      } catch (_e) {
        setLeaveBalance(null);
      } finally {
        setLeaveBalanceLoading(false);
      }
    };
    fetchLeaveBalance();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !editingLeave?.leaveRequestId) return;

    const lt = editingLeave.leaveType || null;
    setLeaveType(lt);
    const categorySelection = resolveLeaveCategorySelection(editingLeave.leaveCategory);
    setLeaveCategory(categorySelection.leaveCategory);
    setCustomLeaveCategory(categorySelection.customLeaveCategory);

    const sd = editingLeave.startDate ? moment(editingLeave.startDate) : null;
    setStartDate(sd?.isValid() ? sd : null);
    const ed = editingLeave.endDate ? moment(editingLeave.endDate) : null;
    setEndDate(ed?.isValid() ? ed : null);

    setStartTime(toHm(editingLeave.startTime));
    setEndTime(toHm(editingLeave.endTime));
    setReason(editingLeave.reason || "");
    setWorkLocation(editingLeave.workLocation || "");
    const isEditingWfh = String(editingLeave.workLocation || "").toLowerCase() === "wfh";
    const stHm = toHm(editingLeave.startTime);
    const enHm = toHm(editingLeave.endTime);

    if (lt === "Half Leave") {
      if (stHm === "13:00" && enHm === "18:00") {
        setStartDaySection({ value: "Second Half", label: "Second Half (1:00 PM - 6:00 PM)" });
      } else {
        setStartDaySection({ value: "First Half", label: "First Half (9:00 AM - 1:00 PM)" });
      }
      setEndDaySection({ value: "Second Half", label: "Second Half (2:00 PM - 6:00 PM)" });
      if (isEditingWfh) {
        setWfhDayKind("half");
      }
    } else if (lt === "Multiple Days Leave") {
      setStartDaySection(
        stHm === "13:00"
          ? { value: "Second Half", label: "Second Half" }
          : { value: "Full Day", label: "Full Day" }
      );
      setEndDaySection(
        enHm === "13:00"
          ? { value: "First Half", label: "First Half" }
          : { value: "Full Day", label: "Full Day" }
      );
    } else {
      applyLeaveTypeDayDefaults(lt);
    }
    if (isEditingWfh && lt !== "Half Leave") {
      const isHalfByTime =
        (stHm === "09:00" && enHm === "13:00") || (stHm === "13:00" && enHm === "18:00");
      if (isHalfByTime) {
        setWfhDayKind("half");
        if (stHm === "13:00" && enHm === "18:00") {
          setStartDaySection({ value: "Second Half", label: "Second Half (1:00 PM - 6:00 PM)" });
        } else {
          setStartDaySection({ value: "First Half", label: "First Half (9:00 AM - 1:00 PM)" });
        }
      } else {
        setWfhDayKind("full");
      }
    }
    setErrors({});
  }, [isOpen, editingLeave, applyLeaveTypeDayDefaults]);

  useEffect(() => {
    if (!isOpen || editingLeave?.leaveRequestId || !initialValues) return;
    setWorkLocation(initialValues.workLocation || "");
    if (String(initialValues.workLocation || "").toLowerCase() === "wfh") {
      setWfhDayKind("full");
      applyLeaveTypeDayDefaults("Full Leave");
    }
  }, [isOpen, editingLeave, initialValues, applyLeaveTypeDayDefaults]);

  useEffect(() => {
    if (!isWfhSimplified || isEditMode) return;
    if (wfhDayKind === "half") {
      applyLeaveTypeDayDefaults("Half Leave");
    } else {
      applyLeaveTypeDayDefaults("Full Leave");
    }
  }, [isWfhSimplified, isEditMode, wfhDayKind, applyLeaveTypeDayDefaults]);

  const resetForm = () => {
    setLeaveType(null);
    setLeaveCategory(null);
    setCustomLeaveCategory("");
    setStartDate(null);
    setStartTime("");
    setEndDate(null);
    setEndTime("");
    setStartDaySection({ value: "Full Day", label: "Full Day" });
    setEndDaySection(null);
    setReason("");
    setWorkLocation("");
    setWfhDayKind("full");
    setErrors({});
    setSubmitLoading(false);
    setLeaveBalance(null);
    setLeaveBalanceLoading(false);
    setShowEarlyLeaveOverrideModal(false);
    setShowContinuousPaidLeaveModal(false);
    setShowUnpaidLeaveModal(false);
    setContinuousLeaveSplitInfo(null);
    setUnpaidLeaveInfo(null);
    setLeavePreview(null);
    setLeavePreviewLoading(false);
  };

  const previewParams = useMemo(() => {
    if (!isOpen || isWfhSimplified || !leaveType || !startDate) return null;
    const isMultiDay = leaveType === "Multiple Days Leave";
    if (isMultiDay && !endDate) return null;
    const params = {
      leaveType,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: (isMultiDay ? endDate : startDate).format("YYYY-MM-DD"),
    };
    if (isMultiDay) {
      if (startDaySection?.value === "Second Half") params.startTime = "13:00";
      if (endDaySection?.value === "First Half") params.endTime = "13:00";
    }
    return params;
  }, [isOpen, isWfhSimplified, leaveType, startDate, endDate, startDaySection, endDaySection]);
  const previewKey = previewParams ? JSON.stringify(previewParams) : null;
  const currentPreview = leavePreview && leavePreview.key === previewKey ? leavePreview.data : null;

  const fetchLeavePreview = useCallback(async (params, key) => {
    const response = await getLeaveRequestPreviewAPI(params);
    const data = response?.data ?? null;
    setLeavePreview((prev) => (data ? { key, data } : prev));
    return data;
  }, []);

  useEffect(() => {
    if (!previewParams) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLeavePreviewLoading(true);
      try {
        await fetchLeavePreview(previewParams, previewKey);
      } catch (_e) {
        // Fallback calculation below still works without the preview.
      } finally {
        if (!cancelled) setLeavePreviewLoading(false);
      }
    }, LEAVE_PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewParams, previewKey, fetchLeavePreview]);

  const ensureLeavePreview = async () => {
    if (currentPreview) return currentPreview;
    if (!previewParams) return null;
    try {
      return await fetchLeavePreview(previewParams, previewKey);
    } catch (_e) {
      return null;
    }
  };

  const getRequestedLeaveDays = useCallback((preview = currentPreview) => {
    if (!startDate) return 0;
    if (preview && !isEarlyLeave(leaveType)) return Number(preview.leaveDays || 0);

    if (leaveType === "Half Leave") return 0.5;
    if (isEarlyLeave(leaveType)) return 0.5;

    const effectiveEndDate =
      leaveType === "Multiple Days Leave" && endDate ? endDate : startDate;
    if (!effectiveEndDate) return 0;

    // Holidays come from the preview; this fallback only skips weekends.
    const first = startDate.clone().startOf("day");
    const last = effectiveEndDate.clone().startOf("day");
    let totalDays = 0;
    for (let cursor = first.clone(); !cursor.isAfter(last, "day"); cursor = cursor.clone().add(1, "day")) {
      const dow = cursor.day();
      if (dow === 0 || dow === 6) continue;
      let fraction = 1;
      if (leaveType === "Multiple Days Leave") {
        if (cursor.isSame(first, "day") && startDaySection?.value === "Second Half") fraction -= 0.5;
        if (cursor.isSame(last, "day") && endDaySection?.value === "First Half") fraction -= 0.5;
      }
      totalDays += Math.max(0, fraction);
    }
    return totalDays;
  }, [currentPreview, leaveType, startDate, endDate, startDaySection, endDaySection]);

  /** Paid days this request would consume against leave balance (early leave and WFH = 0). */
  const getEffectivePaidDaysForRequest = useCallback((preview = currentPreview) => {
    if (isWfhSimplified) return 0;
    if (isEarlyLeave(leaveType)) return 0;
    return getRequestedLeaveDays(preview);
  }, [currentPreview, isWfhSimplified, leaveType, getRequestedLeaveDays]);

  const resolveAvailablePaidLeaves = useCallback(
    (balance, preview = currentPreview) =>
      preview
        ? Math.max(0, Number(preview.availablePaidLeaves || 0))
        : resolveAvailablePaidLeavesForRequest(balance, startDate),
    [currentPreview, startDate]
  );

  const availablePaidLeaves = resolveAvailablePaidLeaves(leaveBalance);
  const effectivePaidDaysForRequest = getEffectivePaidDaysForRequest();
  const unpaidDaysForRequest =
    currentPreview && !isEarlyLeave(leaveType)
      ? Math.max(0, Number(currentPreview.unpaidDays || 0))
      : Math.max(0, effectivePaidDaysForRequest - availablePaidLeaves);
  const paidLeaveFullyExhausted =
    !isWfhSimplified && leaveBalance != null && !leaveBalanceLoading && availablePaidLeaves <= 0;
  const requestWillBeUnpaid =
    !isWfhSimplified &&
    leaveBalance != null &&
    !leaveBalanceLoading &&
    effectivePaidDaysForRequest > 0 &&
    unpaidDaysForRequest > 0;
  const showUnpaidLeaveNotice = !isWfhSimplified && requestWillBeUnpaid;
  const showPaidBalanceExhaustedHint =
    !isWfhSimplified && paidLeaveFullyExhausted && !requestWillBeUnpaid;

  const maybeHandleContinuousPaidLeaveLimit = useCallback((preview = currentPreview) => {
    if (
      isWfhSimplified ||
      leaveType !== "Multiple Days Leave" ||
      !startDate ||
      !endDate
    ) {
      return false;
    }

    const availablePaidLeaves = resolveAvailablePaidLeaves(leaveBalance, preview);
    if (availablePaidLeaves <= MAX_CONTINUOUS_PAID_LEAVES) return false;

    const requestedDays = getRequestedLeaveDays(preview);
    if (requestedDays <= MAX_CONTINUOUS_PAID_LEAVES) return false;

    const paidDays = Math.min(MAX_CONTINUOUS_PAID_LEAVES, requestedDays);
    const unpaidDays = Math.max(0, requestedDays - MAX_CONTINUOUS_PAID_LEAVES);
    setContinuousLeaveSplitInfo({ requestedDays, paidDays, unpaidDays });
    setShowContinuousPaidLeaveModal(true);
    return true;
  }, [
    currentPreview,
    isWfhSimplified,
    leaveType,
    startDate,
    endDate,
    leaveBalance,
    resolveAvailablePaidLeaves,
    getRequestedLeaveDays,
  ]);

  const leaveStartsAfterAccrualStop =
    leaveBalance?.noticePeriodConsumesBalanceOnly &&
    startDate &&
    isLeaveStartAfterAccrualStop(leaveBalance.accrualStopYearMonth, startDate) &&
    getEffectivePaidDaysForRequest() > 0;

  const disablePastDates = (current) => {
    return current && current.isBefore(dateFloor, "day");
  };

  const disableDatesBeforeStart = (current) => {
    if (current && current.isBefore(dateFloor, "day")) {
      return true;
    }

    // Then check if it's before start date (only if start date exists)
    if (startDate && current && current.isBefore(startDate, 'day')) {
      return true;
    }

    // Otherwise, don't disable the date
    return false;
  };

  // Date change handlers
  const handleStartDateChange = (date) => {
    setStartDate(date);

    // If end date exists and is before the new start date, reset it
    if (endDate && date && endDate.isBefore(date)) {
      setEndDate(null);
    }

    // Clear any existing errors for start date
    if (errors.startDate) {
      setErrors({ ...errors, startDate: null });
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);

    // Clear any existing errors for end date
    if (errors.endDate) {
      setErrors({ ...errors, endDate: null });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (isWfhSimplified) {
      if (!startDate) newErrors.startDate = "Date is required";
      if (!reason) newErrors.reason = "Reason is required";
      if (wfhDayKind === "half" && !startDaySection) {
        newErrors.startDaySection = "Please select first half or second half";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    if (!leaveType) newErrors.leaveType = "Leave type is required";
    if (!leaveCategory) {
      newErrors.leaveCategory = "Leave category is required";
    } else if (leaveCategory === LEAVE_CATEGORY_OTHER) {
      const customName = customLeaveCategory.trim();
      if (!customName) {
        newErrors.customLeaveCategory = "Please mention the category name";
      } else if (customName.length > MAX_CUSTOM_LEAVE_CATEGORY_LENGTH) {
        newErrors.customLeaveCategory = `Category name must be ${MAX_CUSTOM_LEAVE_CATEGORY_LENGTH} characters or less`;
      }
    }

    // Date validation
    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!reason) newErrors.reason = "Reason is required";

    if (showStartTime && !startTime)
      newErrors.startTime = "Start time is required";
    if (showEndDate && !endDate)
      newErrors.endDate = "End date is required";
    if (showEndTime && !endTime)
      newErrors.endTime = "End time is required";

    // New validation for day sections
    if (showDaySections && !startDaySection)
      newErrors.startDaySection = "Please select start day section";
    if (showDaySections && showEndDate && !endDaySection)
      newErrors.endDaySection = "Please select end day section";

    // Validate end time is after start time for same day leaves
    if (startTime && endTime && (!endDate || endDate === startDate)) {
      const startDateTime = new Date(`${startDate.format('YYYY-MM-DD')}T${startTime}`);
      const endDateTime = new Date(`${startDate.format('YYYY-MM-DD')}T${endTime}`);
      if (endDateTime <= startDateTime) {
        newErrors.endTime = "End time must be after start time";
      }
    }
    if (isEarlyLeave(leaveType) && startDate && startTime && endTime) {
      const startMoment = moment(`${startDate.format("YYYY-MM-DD")} ${startTime}`, "YYYY-MM-DD HH:mm", true);
      const endMoment = moment(`${startDate.format("YYYY-MM-DD")} ${endTime}`, "YYYY-MM-DD HH:mm", true);
      if (!startMoment.isValid() || !endMoment.isValid()) {
        newErrors.endTime = "Please enter valid early leave times";
      } else {
        const durationMinutes = endMoment.diff(startMoment, "minutes");
        if (durationMinutes <= 0) {
          newErrors.endTime = "End time must be after start time";
        } else if (durationMinutes > 120) {
          newErrors.endTime = "Early leave max. duration is 2 hours";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const resolveEarlyLeaveAlreadyUsed = async () => {
    let balance = leaveBalance;
    if (!balance || balance.earlyLeaveEligibleThisMonth === undefined) {
      try {
        const response = await getMyLeaveBalanceAPI();
        balance = response?.data?.data ?? response?.data ?? null;
        if (balance) setLeaveBalance(balance);
      } catch (_e) {
        return earlyLeaveAlreadyUsedForNewApply;
      }
    }
    const usedCount = Number(balance?.monthlyEarlyLeaveUsedCount ?? 0);
    const eligible = balance?.earlyLeaveEligibleThisMonth !== false && usedCount < 1;
    return !eligible;
  };

  const refreshLeaveBalance = async () => {
    try {
      const response = await getMyLeaveBalanceAPI();
      const balance = response?.data?.data ?? response?.data ?? null;
      if (balance) setLeaveBalance(balance);
      return balance;
    } catch (_e) {
      return leaveBalance;
    }
  };

  const buildUnpaidLeaveInfo = (balance, preview) => {
    const effectiveDays = getEffectivePaidDaysForRequest(preview);
    if (preview && !isEarlyLeave(leaveType)) {
      const unpaidDays = Math.max(0, Number(preview.unpaidDays || 0));
      if (effectiveDays <= 0 || unpaidDays <= 0) return null;
      const paidDays = Math.max(0, Number(preview.paidDays || 0));
      return { requestedDays: effectiveDays, paidDays, unpaidDays, fullyUnpaid: paidDays <= 0 };
    }
    const available = resolveAvailablePaidLeaves(balance, preview);
    const unpaidDays = Math.max(0, effectiveDays - available);
    if (effectiveDays <= 0 || unpaidDays <= 0) return null;
    const paidDays = Math.min(available, effectiveDays);
    return {
      requestedDays: effectiveDays,
      paidDays,
      unpaidDays,
      fullyUnpaid: available <= 0,
    };
  };

  const maybeHandleUnpaidLeave = async (preview = currentPreview) => {
    if (isWfhSimplified || isEditMode) return false;

    let balance = leaveBalance;
    if (!balance || balance.availableLeaves === undefined) {
      balance = await refreshLeaveBalance();
    }
    const info = buildUnpaidLeaveInfo(balance, preview);
    if (!info) return false;

    setUnpaidLeaveInfo(info);
    setShowUnpaidLeaveModal(true);
    return true;
  };

  const handleButtonClick = async () => {
    if (submitLoading) return;
    if (!validateForm()) {
      showErrorToast("Please fix the errors in the form");
      return;
    }
    const preview = isWfhSimplified ? null : await ensureLeavePreview();
    if (isPreviewAllNonWorking(preview)) {
      const message = "The selected date(s) fall on weekends or public holidays, so no leave is required.";
      setErrors((prev) => ({ ...prev, startDate: message }));
      showErrorToast(message);
      return;
    }
    if (isEarlyLeave(leaveType) && !isEditMode) {
      const alreadyUsed = await resolveEarlyLeaveAlreadyUsed();
      if (alreadyUsed) {
        setShowEarlyLeaveOverrideModal(true);
        return;
      }
    }
    if (await maybeHandleUnpaidLeave(preview)) {
      return;
    }
    if (maybeHandleContinuousPaidLeaveLimit(preview)) {
      return;
    }
    await handleSubmit();
  };

  const handleSubmit = async ({ countEarlyAsHalfDay = false } = {}) => {
    if (submitLoading) return;
    setSubmitLoading(true);
    let leaveData;

    if (isWfhSimplified) {
      leaveData = {
        leaveType: WFH_REQUEST_TYPE,
        leaveCategory: WFH_REQUEST_TYPE,
        reason,
        startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
        endDate: startDate ? startDate.format("YYYY-MM-DD") : "",
        workLocation: "wfh",
      };
      if (wfhDayKind === "half" && startDaySection?.value === "First Half") {
        leaveData.startTime = "09:00";
        leaveData.endTime = "13:00";
      } else if (wfhDayKind === "half" && startDaySection?.value === "Second Half") {
        leaveData.startTime = "13:00";
        leaveData.endTime = "18:00";
      }
    } else {
      const resolvedLeaveCategory =
        leaveCategory === LEAVE_CATEGORY_OTHER
          ? customLeaveCategory.trim()
          : leaveCategory;
      // Create base payload (JWT auth handles identity; server sends mail via SMTP if configured)
      leaveData = {
        leaveType,
        leaveCategory: resolvedLeaveCategory,
        reason,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
        workLocation: workLocation || undefined,
      };

      // Add optional fields only if they exist and are required for the leave type
      if (showStartTime && startTime) {
        leaveData.startTime = startTime;
      }

      if (showEndDate && endDate) {
        leaveData.endDate = endDate.format('YYYY-MM-DD');
      } else {
        // For single day leaves, end date is same as start date
        leaveData.endDate = startDate ? startDate.format('YYYY-MM-DD') : '';
      }

      if (showEndTime && endTime) {
        leaveData.endTime = endTime;
      }

      // Add day section data and set times based on selections
      if (showDaySections) {
        // Set specific times based on day sections for Multiple Days Leave
        if (leaveType === "Multiple Days Leave") {
          if (startDaySection.value === "Second Half") {
            leaveData.startTime = "13:00";
          }
          if (endDaySection?.value === "First Half") {
            leaveData.endTime = "13:00";
          }
        }

        // Set specific times based on half day selection for Half Leave
        if (leaveType === "Half Leave") {
          if (startDaySection.value === "First Half") {
            leaveData.startTime = "09:00";
            leaveData.endTime = "13:00";
          } else if (startDaySection.value === "Second Half") {
            leaveData.startTime = "13:00";
            leaveData.endTime = "18:00";
          }
        }
      }

      if (countEarlyAsHalfDay && isEarlyLeave(leaveType) && startDate) {
        const requestedStart = moment(startTime || "09:00", "HH:mm", true);
        const useSecondHalf = requestedStart.isValid() && requestedStart.hour() >= 13;
        leaveData.leaveType = "Half Leave";
        leaveData.startDate = startDate.format("YYYY-MM-DD");
        leaveData.endDate = startDate.format("YYYY-MM-DD");
        if (useSecondHalf) {
          leaveData.startTime = "13:00";
          leaveData.endTime = "18:00";
        } else {
          leaveData.startTime = "09:00";
          leaveData.endTime = "13:00";
        }
      }
    }

    try {
      if (isEditMode && editingLeave?.leaveRequestId) {
        await dispatch(
          updateOwnLeaveRequestAPI({
            leaveRequestId: editingLeave.leaveRequestId,
            ...leaveData,
          })
        ).unwrap();
      } else {
        await dispatch(createLeaveRequestAPI(leaveData)).unwrap();
      }
      toggleLeaveDrawer();
    } catch (_e) {
      // Error toast from leave reducer
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleConfirmEarlyLeaveAsHalfDay = async () => {
    setShowEarlyLeaveOverrideModal(false);
    showSuccessToast("Monthly Early Leave already used. This request will be counted as Half Leave (0.5 day).");
    if (await maybeHandleUnpaidLeave()) {
      return;
    }
    await handleSubmit({ countEarlyAsHalfDay: true });
  };

  const handleConfirmContinuousPaidLeave = async () => {
    setShowContinuousPaidLeaveModal(false);
    await handleSubmit();
  };

  const handleConfirmUnpaidLeave = async () => {
    setShowUnpaidLeaveModal(false);
    if (maybeHandleContinuousPaidLeaveLimit()) {
      return;
    }
    await handleSubmit();
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#4f46e5' : (state.hasError ? '#ef4444' : '#e5e7eb'),
      boxShadow: state.isFocused ? '0 0 0 1px #4f46e5' : 'none',
      backgroundColor: '#ffffff',
      '&:hover': {
        borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
      },
      minHeight: '42px',
      borderRadius: '0.375rem',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 4000 }),
    menu: (base) => ({ ...base, backgroundColor: '#ffffff', zIndex: 4000 }),
    menuList: (base) => ({ ...base, backgroundColor: '#ffffff' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#4f46e5' : (state.isFocused ? '#e0e7ff' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : '#111827',
      cursor: 'pointer',
      padding: '8px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1f2937',
    }),
  };

  // Options for day sections
  const daySectionOptions = [
    { value: "Full Day", label: "Full Day" },
    { value: "First Half", label: "First Half" },
    { value: "Second Half", label: "Second Half" },
  ];

  // Half day section options - only First Half and Second Half
  const halfDaySectionOptions = [
    { value: "First Half", label: "First Half (9:00 AM - 1:00 PM)" },
    { value: "Second Half", label: "Second Half (1:00 PM - 6:00 PM)" },
  ];

  const drawerTitle = (() => {
    if (isWfhSimplified) {
      return isEditMode ? "Edit work from home" : "Request work from home";
    }
    return isEditMode ? "Edit leave request" : "Apply Leave";
  })();

  const submitButtonTitle = (() => {
    if (isWfhSimplified) {
      return isEditMode ? "Save changes" : "Submit WFH request";
    }
    return isEditMode ? "Save changes" : "Apply For Leave";
  })();

  const workLocationOptions = isEmployeeUser
    ? [
      { value: "", label: "Not specified" },
      { value: "wfh", label: "WFH (work from home)" },
    ]
    : [
      { value: "", label: "Not specified" },
      { value: "wfh", label: "WFH (work from home)" },
      { value: "wfs", label: "WFS (work on site)" },
      { value: "wfo", label: "WFO (work from office)" },
    ];

  return (
    <div>
      <Drawer
        open={isOpen}
        onClose={toggleLeaveDrawer}
        placement={window.innerWidth > 700 ? "right" : "bottom"}
        className="drawer"
        title={drawerTitle}
        width="600px"
      >
        <div className="p-4 flex flex-col gap-4">
          <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900">
            {leaveBalanceLoading ? (
              <p className="m-0">Loading leave balance...</p>
            ) : leaveBalance ? (
              <div className="flex flex-col gap-1">
                <p className="m-0 font-semibold">
                  Leave balance: {Math.max(0, Number(leaveBalance.availableLeaves || 0)).toFixed(2)} days
                  {leaveBalance.availableLeavesForNewLeave != null &&
                    Number(leaveBalance.availableLeavesForNewLeave) !== Number(leaveBalance.availableLeaves) ? (
                    <span className="font-normal text-indigo-700">
                      {" "}
                      (usable now: {Math.max(0, Number(leaveBalance.availableLeavesForNewLeave || 0)).toFixed(2)})
                    </span>
                  ) : null}
                </p>
                <p className="m-0 text-xs text-indigo-700">
                  FY {leaveBalance.financeYearStartYear}-{Number(leaveBalance.financeYearStartYear) + 1}
                  {" "} | Accrued: {Number(leaveBalance.accruedLeaves || 0).toFixed(2)}
                  {" "} | Paid used: {Number(leaveBalance.paidLeaveUsed ?? 0).toFixed(2)}
                  {Number(leaveBalance.totalUsedLeaves || 0) > Number(leaveBalance.paidLeaveUsed || 0) ? (
                    <>
                      {" "} | Total leave taken: {Number(leaveBalance.totalUsedLeaves || 0).toFixed(2)}
                    </>
                  ) : null}
                  {leaveBalance.accrualStopYearMonth
                    ? ` | Accrual until: ${leaveBalance.accrualStopYearMonth}`
                    : ""}
                  {Number(leaveBalance.unpaidLeaveDays || 0) > 0
                    ? ` | Unpaid: ${Number(leaveBalance.unpaidLeaveDays).toFixed(2)}`
                    : ""}
                </p>
                {leaveBalance.noticePeriodConsumesBalanceOnly ? (
                  <p className="m-0 text-xs text-amber-800">
                    Paid leave accrual has ended (notice period). New leave will use your remaining
                    paid balance first; any days beyond your balance will be unpaid.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="m-0">Leave balance is not available right now.</p>
            )}
          </div>
          {leaveStartsAfterAccrualStop && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              You are in your notice period. This leave will deduct from your remaining paid leave
              balance ({Math.max(0, Number(availablePaidLeaves || 0)).toFixed(2)} day(s) left).
              Any extra days will be unpaid and deducted from salary.
            </div>
          )}
          {showPaidBalanceExhaustedHint && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
              Your paid leave balance is exhausted. New leave requests will be recorded as unpaid leave.
            </div>
          )}
          {showUnpaidLeaveNotice && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
              {paidLeaveFullyExhausted || unpaidDaysForRequest >= effectivePaidDaysForRequest
                ? `Your paid leave balance is exhausted. This request (${effectivePaidDaysForRequest.toFixed(2)} day) will be unpaid leave.`
                : `You only have ${availablePaidLeaves.toFixed(2)} paid leave left. ${unpaidDaysForRequest.toFixed(2)} day of this request will be unpaid leave.`}
            </div>
          )}
          {isWfhSimplified ? (
            <>

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <DatePicker
                  className={`w-full p-2 border rounded-md focus:outline-nonefocus:ring-2 focus:ring-indigo-500 ${errors.startDate ? "border-red-500" : "border-gray-300"}`}
                  disabledDate={disablePastDates}
                  onChange={handleStartDateChange}
                  value={startDate}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                />
                <ErrorText show={!!errors.startDate} message={errors.startDate} />
              </div>

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  value={wfhDayKind}
                  onChange={(e) => setWfhDayKind(e.target.value)}
                >
                  <option value="full">Full day</option>
                  <option value="half">Half day</option>
                </select>
              </div>

              {wfhDayKind === "half" && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Which half
                  </label>
                  <Select
                    value={startDaySection}
                    options={halfDaySectionOptions}
                    onChange={(option) => setStartDaySection(option)}
                    placeholder="First half or second half"
                    styles={{
                      ...customSelectStyles,
                      control: (provided) => ({
                        ...customSelectStyles.control(provided, { isFocused: false }),
                        borderColor: errors.startDaySection ? "var(--primary-color)" : "#e5e7eb",
                      }),
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                  <ErrorText show={!!errors.startDaySection} message={errors.startDaySection} />
                </div>
              )}

              <Input
                placeholder="Reason for working from home"
                label="Reason"
                type="text"
                name="reason"
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={errors.reason}
              />
              <ErrorText show={!!errors.reason} message={errors.reason} />
            </>
          ) : (
            <>
              <SelectInput
                label="Leave Type"
                selected={leaveType}
                placeholder="Select leave type"
                type="leave"
                onChange={(value) => {
                  const v = value?.value;
                  setLeaveType(v);
                  applyLeaveTypeDayDefaults(v);
                }}
                error={errors.leaveType}
              />
              <ErrorText show={!!errors.leaveType} message={errors.leaveType} />

              {showEarlyLeaveNotice && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {earlyLeaveAlreadyUsedForNewApply
                    ? "You already used Early Leave this month. If you continue, this request will be counted as Half Leave (0.5 day) after confirmation."
                    : "Early leave is allowed once per month (max. 2 hours). Your first Early Leave this month is free; a second request in the same month will count as Half Leave (0.5 day)."}
                </div>
              )}

              <SelectInput
                label="Leave Category"
                selected={leaveCategory}
                placeholder="Select leave category"
                type="leaveType"
                onChange={(value) => {
                  const next = value?.value ?? null;
                  setLeaveCategory(next);
                  if (next !== LEAVE_CATEGORY_OTHER) {
                    setCustomLeaveCategory("");
                    if (errors.customLeaveCategory) {
                      setErrors((prev) => ({ ...prev, customLeaveCategory: null }));
                    }
                  }
                  if (errors.leaveCategory) {
                    setErrors((prev) => ({ ...prev, leaveCategory: null }));
                  }
                }}
                error={errors.leaveCategory}
              />
              <ErrorText
                show={!!errors.leaveCategory}
                message={errors.leaveCategory}
              />

              {leaveCategory === LEAVE_CATEGORY_OTHER ? (
                <>
                  <Input
                    placeholder="Mention category name"
                    label="Other category name"
                    type="text"
                    name="customLeaveCategory"
                    id="customLeaveCategory"
                    value={customLeaveCategory}
                    onChange={(e) => {
                      setCustomLeaveCategory(e.target.value);
                      if (errors.customLeaveCategory) {
                        setErrors((prev) => ({ ...prev, customLeaveCategory: null }));
                      }
                    }}
                    error={errors.customLeaveCategory}
                  />
                  <ErrorText
                    show={!!errors.customLeaveCategory}
                    message={errors.customLeaveCategory}
                  />
                </>
              ) : null}

              {/* <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Work location (optional)
                </label>
                <select
                  className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white ${
                    workLocation ? "text-gray-900" : "text-gray-400"
                  }`}
                  value={workLocation}
                  onChange={(e) => setWorkLocation(e.target.value)}
                >
                  {workLocationOptions.map((option) => (
                    <option key={option.value || "not-specified"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div> */}

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <DatePicker
                  className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                  disabledDate={disablePastDates}
                  onChange={handleStartDateChange}
                  value={startDate}
                  format="YYYY-MM-DD"
                  placeholder="Select start date"
                />
                <ErrorText show={!!errors.startDate} message={errors.startDate} />
              </div>

              {showStartTime && (
                <>
                  <Input
                    placeholder="Select Start Time"
                    label="Start Time"
                    type="time"
                    name="startTime"
                    id="startTime"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    error={errors.startTime}
                  />
                  <ErrorText show={!!errors.startTime} message={errors.startTime} />
                </>
              )}

              {showHalfDayOptions && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Half Day Section
                  </label>
                  <Select
                    value={startDaySection}
                    options={halfDaySectionOptions}
                    onChange={(option) => setStartDaySection(option)}
                    placeholder="Select half day section"
                    styles={{
                      ...customSelectStyles,
                      control: (provided) => ({
                        ...customSelectStyles.control(provided, { isFocused: false }),
                        borderColor: errors.startDaySection ? 'var(--primary-color)' : '#e5e7eb',
                      })
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                  <ErrorText show={!!errors.startDaySection} message={errors.startDaySection} />
                </div>
              )}

              {showDaySections && !showHalfDayOptions && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Day Section
                  </label>
                  <Select
                    value={startDaySection}
                    options={daySectionOptions.filter(opt => opt.value !== "First Half")}
                    onChange={(option) => setStartDaySection(option)}
                    placeholder="Select start day section"
                    styles={{
                      ...customSelectStyles,
                      control: (provided) => ({
                        ...customSelectStyles.control(provided, { isFocused: false }),
                        borderColor: errors.startDaySection ? 'var(--primary-color)' : '#e5e7eb',
                      })
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                  <ErrorText show={!!errors.startDaySection} message={errors.startDaySection} />
                </div>
              )}

              {showEndDate && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <DatePicker
                    className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.endDate ? 'border-red-500' : 'border-gray-300'}`}
                    disabledDate={disableDatesBeforeStart}
                    onChange={handleEndDateChange}
                    value={endDate}
                    format="YYYY-MM-DD"
                    placeholder="Select end date"
                    disabled={!startDate}
                  />
                  <ErrorText show={!!errors.endDate} message={errors.endDate} />
                </div>
              )}

              {showEndTime && (
                <>
                  <Input
                    placeholder="Select End Time"
                    label="End Time"
                    type="time"
                    name="endTime"
                    id="endTime"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    error={errors.endTime}
                  />
                  <ErrorText show={!!errors.endTime} message={errors.endTime} />
                </>
              )}

              {showDaySections && showEndDate && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Day Section
                  </label>
                  <Select
                    value={endDaySection}
                    options={daySectionOptions.filter(opt => opt.value !== "Second Half")}
                    onChange={(option) => setEndDaySection(option)}
                    placeholder="Select end day section"
                    styles={{
                      ...customSelectStyles,
                      control: (provided) => ({
                        ...customSelectStyles.control(provided, { isFocused: false }),
                        borderColor: errors.endDaySection ? 'var(--primary-color)' : '#e5e7eb',
                      })
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    isDisabled={!endDate}
                  />
                  <ErrorText show={!!errors.endDaySection} message={errors.endDaySection} />
                </div>
              )}

              {previewParams && (leavePreviewLoading || currentPreview) && (
                <LeaveDayCountNotice
                  preview={currentPreview}
                  loading={leavePreviewLoading && !currentPreview}
                  showPaidSplit={!isEarlyLeave(leaveType)}
                />
              )}

              <Input
                placeholder="Enter reason for leave"
                label="Reason"
                type="text"
                name="reason"
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={errors.reason}
              />
              <ErrorText show={!!errors.reason} message={errors.reason} />
            </>
          )}

          <div className="flex justify-end mt-4">
            {submitLoading ? (
              <Loader className="!h-0" />
            ) : (
              <AddButton
                buttonTitle={submitButtonTitle}
                image="/svg/leave.svg"
                onclickDrawer={handleButtonClick}
              />
            )}
          </div>
        </div>
      </Drawer>
      <ConfirmModal
        isOpen={showEarlyLeaveOverrideModal}
        zIndex={4100}
        onClose={() => {
          if (!submitLoading) setShowEarlyLeaveOverrideModal(false);
        }}
        onConfirm={handleConfirmEarlyLeaveAsHalfDay}
        title="Early Leave Already Used"
        content="You have already used Early Leave for this month. If you continue, this request will be counted as Half Leave (0.5 day)."
        confirmText="Yes, count as Half Leave"
        cancelText="No"
        confirmLoading={submitLoading}
      />
      <ConfirmModal
        isOpen={showContinuousPaidLeaveModal}
        zIndex={4100}
        onClose={() => {
          if (!submitLoading) setShowContinuousPaidLeaveModal(false);
        }}
        onConfirm={handleConfirmContinuousPaidLeave}
        title="Paid Leave Limit For Continuous Leave"
        content={
          continuousLeaveSplitInfo
            ? `You applied for ${continuousLeaveSplitInfo.requestedDays} continuous leave days. You can only use ${continuousLeaveSplitInfo.paidDays} paid leave continuously, and ${continuousLeaveSplitInfo.unpaidDays} day(s) will be unpaid.`
            : "You can only use 6 paid leave continuously. Extra leave days will be unpaid."
        }
        confirmText="Continue"
        cancelText="Cancel"
        confirmLoading={submitLoading}
      />
      <ConfirmModal
        isOpen={showUnpaidLeaveModal}
        zIndex={4100}
        onClose={() => {
          if (!submitLoading) setShowUnpaidLeaveModal(false);
        }}
        onConfirm={handleConfirmUnpaidLeave}
        title="Paid Leave Exhausted"
        content={
          unpaidLeaveInfo?.fullyUnpaid
            ? `Your paid leave balance is exhausted. This leave request (${unpaidLeaveInfo.requestedDays} day) will be recorded as unpaid leave. Do you want to continue?`
            : unpaidLeaveInfo
              ? `You have ${unpaidLeaveInfo.paidDays} paid leave day available. ${unpaidLeaveInfo.unpaidDays} day of this ${unpaidLeaveInfo.requestedDays} day request will be unpaid. Do you want to continue?`
              : "Your paid leave balance is exhausted. This leave will be recorded as unpaid leave. Do you want to continue?"
        }
        confirmText="Continue as Unpaid Leave"
        cancelText="Cancel"
        confirmLoading={submitLoading}
      />
    </div>
  );
};

export default ApplyLeavedrawer;
